import { Eye, EyeOff, RotateCcw, Save } from "lucide-react";
import * as React from "react";

import { type PublishedFormMeta, formsApi } from "@/api/forms";
import { overridesApi } from "@/api/overrides";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useResolvedSchema } from "@/hooks/useResolvedField";
import { useUIStore } from "@/stores/ui";

/**
 * PersonalOverrides — user chỉnh override cá nhân cho form đã build (Doc 23 §13.1).
 *
 * 2-3 click:
 * - Chọn form code.
 * - Set default value cho field thường nhập.
 * - Ẩn field không cần (nếu lock_level cho phép).
 * - Reset về tenant default.
 */
export function PersonalOverrides(): JSX.Element {
  const [forms, setForms] = React.useState<PublishedFormMeta[]>([]);
  const [activeCode, setActiveCode] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const pushToast = useUIStore((s) => s.pushToast);
  const { schema, reload, loading } = useResolvedSchema(activeCode);

  React.useEffect(() => {
    void (async () => {
      try {
        const list = await formsApi.listPublished();
        setForms(list);
        if (list.length > 0 && !activeCode) {
          setActiveCode(list[0]?.code ?? null);
        }
      } catch (e) {
        pushToast({
          variant: "destructive",
          title: "Lỗi tải forms",
          description: e instanceof Error ? e.message : String(e),
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateOverride = async (
    fieldName: string,
    overrides: Record<string, unknown>,
  ): Promise<void> => {
    if (!activeCode) return;
    setBusy(true);
    try {
      const res = await overridesApi.upsertPersonalOverride(
        activeCode,
        fieldName,
        overrides,
      );
      pushToast({
        variant: "success",
        title: "Đã lưu",
        description:
          res.rejected_keys.length > 0
            ? `Reject: ${res.rejected_keys.join(", ")}`
            : undefined,
      });
      reload();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const reset = async (fieldName: string): Promise<void> => {
    if (!activeCode) return;
    setBusy(true);
    try {
      await overridesApi.deletePersonalOverride(activeCode, fieldName);
      pushToast({ variant: "success", title: "Đã reset" });
      reload();
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Reset thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Override cá nhân</h1>
        <p className="text-sm text-muted-foreground">
          Tinh chỉnh form cho riêng tài khoản của bạn — đặt default, ẩn field
          không cần. Áp dụng ngay khi AI render form lần tới.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Chọn form</CardTitle>
        </CardHeader>
        <CardContent>
          {forms.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Chưa có form nào published.
            </p>
          ) : (
            <select
              className="w-full h-9 rounded-md border bg-background px-3 text-sm"
              value={activeCode ?? ""}
              onChange={(e) => setActiveCode(e.target.value || null)}
            >
              {forms.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.name} ({f.code} v{f.version})
                </option>
              ))}
            </select>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="shimmer h-4 w-1/3 rounded" />
            <div className="shimmer h-3 w-1/2 rounded" />
            <div className="shimmer h-12 w-full rounded" />
            <div className="shimmer h-12 w-full rounded" />
          </CardContent>
        </Card>
      ) : schema ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Field của {schema.code}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {schema.fields.map((f) => (
              <FieldRow
                key={f.name}
                field={f}
                disabled={busy}
                onUpdate={(ovr) => void updateOverride(f.name, ovr)}
                onReset={() => void reset(f.name)}
              />
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

interface FieldRowProps {
  field: {
    name: string;
    label: string;
    type: string;
    required?: boolean;
    default?: unknown;
    hidden?: boolean;
  };
  disabled: boolean;
  onUpdate: (overrides: Record<string, unknown>) => void;
  onReset: () => void;
}

function FieldRow({ field, disabled, onUpdate, onReset }: FieldRowProps): JSX.Element {
  const [defaultVal, setDefaultVal] = React.useState(
    field.default == null ? "" : String(field.default),
  );

  return (
    <div className="rounded border bg-muted/20 p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium text-sm">{field.label}</span>
        <code className="text-[10px] bg-background px-1 rounded">
          {field.name}
        </code>
        <Badge variant="outline" className="text-[10px]">
          {field.type}
        </Badge>
        {field.required ? (
          <Badge variant="destructive" className="text-[10px]">
            bắt buộc
          </Badge>
        ) : null}
      </div>

      <div className="flex items-center gap-2">
        <label className="text-xs text-muted-foreground">Default:</label>
        <Input
          value={defaultVal}
          onChange={(e) => setDefaultVal(e.target.value)}
          placeholder="(để trống = không set default)"
          className="h-7 text-xs flex-1"
          disabled={disabled}
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            onUpdate({
              default: defaultVal === "" ? null : defaultVal,
            })
          }
          disabled={disabled}
        >
          <Save className="size-3 mr-1" /> Lưu
        </Button>
      </div>

      <div className="flex items-center gap-2 text-xs">
        <Button
          size="sm"
          variant={field.hidden ? "default" : "outline"}
          onClick={() => onUpdate({ hidden: !field.hidden })}
          disabled={disabled}
        >
          {field.hidden ? (
            <>
              <Eye className="size-3 mr-1" /> Hiện lại
            </>
          ) : (
            <>
              <EyeOff className="size-3 mr-1" /> Ẩn cho tôi
            </>
          )}
        </Button>
        <Button size="sm" variant="ghost" onClick={onReset} disabled={disabled}>
          <RotateCcw className="size-3 mr-1" /> Reset
        </Button>
      </div>
    </div>
  );
}
